import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { AlertCircle, CheckCircle, XCircle } from 'lucide-react';

const Debug = () => {
  const [status, setStatus] = useState({
    loading: true,
    firebaseConnected: false,
    totalCars: 0,
    availableCars: 0,
    cars: [],
    error: null
  });

  useEffect(() => {
    const testConnection = async () => {
      try {
        // Teste 1: Ler TODOS os carros
        console.log('🔍 Testando conexão...');
        const allCarsRef = collection(db, 'cars');
        const allCarsSnapshot = await getDocs(allCarsRef);

        const allCars = allCarsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        console.log('Total de carros:', allCars.length);
        console.log('Carros:', allCars);

        // Teste 2: Ler carros não vendidos
        const availableCarsQuery = query(
          collection(db, 'cars'),
          where('vendido', '==', false),
          orderBy('createdAt', 'desc')
        );

        const availableCarsSnapshot = await getDocs(availableCarsQuery);
        const availableCars = availableCarsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        console.log('Carros disponíveis:', availableCars.length);
        console.log('Carros disponíveis:', availableCars);

        setStatus({
          loading: false,
          firebaseConnected: true,
          totalCars: allCars.length,
          availableCars: availableCars.length,
          cars: allCars,
          error: null
        });

      } catch (error) {
        console.error('❌ Erro:', error);
        setStatus({
          loading: false,
          firebaseConnected: false,
          totalCars: 0,
          availableCars: 0,
          cars: [],
          error: error.message
        });
      }
    };

    testConnection();
  }, []);

  if (status.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-block mb-4">
            <div className="w-16 h-16 border-4 border-blue-200 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin absolute top-0"></div>
          </div>
          <p className="text-gray-600">Testando conexão com Firebase...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">
          Debug - Integração Firebase
        </h1>

        {/* Status da Conexão */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Status da Conexão
          </h2>

          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              {status.firebaseConnected ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <XCircle className="w-6 h-6 text-red-600" />
              )}
              <span className="font-medium">
                Firebase: {status.firebaseConnected ? 'Conectado' : 'Desconectado'}
              </span>
            </div>

            <div className="flex items-center space-x-3">
              <AlertCircle className="w-6 h-6 text-blue-600" />
              <span>
                Projeto: <strong>{process.env.REACT_APP_FIREBASE_PROJECT_ID}</strong>
              </span>
            </div>
          </div>
        </div>

        {/* Erro */}
        {status.error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 mb-6">
            <div className="flex items-start space-x-3">
              <XCircle className="w-6 h-6 text-red-600 mt-1" />
              <div>
                <h3 className="font-bold text-red-900 mb-2">Erro de Conexão</h3>
                <p className="text-red-700 mb-4">{status.error}</p>

                {status.error.includes('permission') && (
                  <div className="bg-white rounded-lg p-4">
                    <p className="font-semibold text-gray-900 mb-2">
                      ⚠️ Problema de Permissões do Firestore
                    </p>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                      <li>Vá ao Firebase Console</li>
                      <li>Firestore Database → Rules</li>
                      <li>Copie o conteúdo de <code className="bg-gray-100 px-2 py-0.5 rounded">firestore.rules</code></li>
                      <li>Clique em "Publish"</li>
                    </ol>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="text-sm text-gray-600 mb-1">Total de Carros</div>
            <div className="text-4xl font-bold text-gray-900">{status.totalCars}</div>
            <div className="text-sm text-gray-500 mt-1">Na base de dados</div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="text-sm text-gray-600 mb-1">Carros Disponíveis</div>
            <div className="text-4xl font-bold text-blue-600">{status.availableCars}</div>
            <div className="text-sm text-gray-500 mt-1">Não vendidos (vendido=false)</div>
          </div>
        </div>

        {/* Lista de Carros */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Carros na Base de Dados
          </h2>

          {status.cars.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Nenhum carro encontrado na base de dados</p>
              <p className="text-sm mt-2">Adicione um carro no sistema DCBO primeiro</p>
            </div>
          ) : (
            <div className="space-y-3">
              {status.cars.map((car, index) => (
                <div
                  key={car.id || index}
                  className={`p-4 rounded-xl border-2 ${
                    car.vendido === false
                      ? 'bg-green-50 border-green-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-gray-900">
                        {car.marca} {car.modelo}
                      </h3>
                      <div className="text-sm text-gray-600 space-y-1 mt-1">
                        <p>Preço: €{car.preco?.toLocaleString('pt-PT')}</p>
                        <p>Ano: {car.ano} | KM: {car.km?.toLocaleString('pt-PT')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {car.vendido === false ? (
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                          ✅ Disponível
                        </span>
                      ) : (
                        <span className="bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                          ❌ Vendido
                        </span>
                      )}
                      <div className="text-xs text-gray-500 mt-2">
                        vendido: {String(car.vendido)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instruções */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 mt-6">
          <h3 className="font-bold text-blue-900 mb-3">💡 Como Resolver</h3>
          <div className="space-y-2 text-sm text-blue-800">
            <p><strong>Se não aparecem carros:</strong></p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Adicione um carro no sistema DCBO</li>
              <li>Certifique-se que está marcado como "Não vendido"</li>
            </ul>

            <p className="mt-4"><strong>Se aparecem carros mas com vendido=true:</strong></p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Vá ao DCBO e edite o carro</li>
              <li>Desmarque a opção "Vendido"</li>
              <li>Salve as alterações</li>
            </ul>

            <p className="mt-4"><strong>Se há erro de permissões:</strong></p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Configure as regras do Firestore (veja firestore.rules)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Debug;
